import { useMemo } from 'react';
import { Button } from '@/components/button';
import {
  QuizDisplayActivityProps as BaseProps,
  QuizWidgetActivity,
} from '../types';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/utils';
import { QuizPlayerScreen } from '@/components/quiz-player';

export type QuizActivityItemProps = {
  className?: string;
  data: QuizWidgetActivity;
  onSelect?: () => void;
  disabled?: boolean;
};

type QuizDisplayActivityProps = React.ComponentProps<'div'> &
  BaseProps & {
    slots?: { ActivityItem?: React.ElementType<QuizActivityItemProps> };
  };

export function QuizDisplayActivity({
  data = [],
  onActivitySelect = () => {},
  disabled = [],
  slots = {},
  ...others
}: QuizDisplayActivityProps) {
  const { ActivityItem = QuizActivityItem } = slots;

  return (
    <QuizPlayerScreen {...others} variant="welcome">
      <ol className="mm:flex mm:flex-col mm:gap-3 mm:list-none">
        {data.map((quiz, index) => (
          <ActivityItem
            key={index}
            data={quiz}
            onSelect={() => onActivitySelect(quiz.id)}
            disabled={disabled.includes(quiz.id)}
          />
        ))}
      </ol>
    </QuizPlayerScreen>
  );
}

function QuizActivityItem({
  data,
  className,
  onSelect = () => {},
  disabled,
  ...others
}: React.ComponentProps<'li'> & QuizActivityItemProps) {
  const { t } = useTranslation();

  const { attempts } = data;

  const bestScore = useMemo(
    () =>
      attempts.length
        ? Math.round(
            Math.max(...attempts.map((attempt) => attempt.outcome.score ?? 0))
          )
        : 0,
    [attempts]
  );

  const scoreLabel = t('quizBestScore', {
    fallback: 'Punteggio più alto: {score}%',
    args: { score: bestScore },
  });

  return (
    <li
      {...others}
      className={cn(
        'mm:flex mm:items-center mm:w-full mm:p-4 mm:rounded-lg mm:bg-transparent mm:border-border mm:border-2 mm:text-foreground mm:gap-2',
        className
      )}
    >
      <div className="mm:flex-1 mm:flex mm:flex-col">
        <span className="mm:font-semibold">{data.name}</span>
        <span className="mm:text-sm mm:text-muted-foreground">
          {scoreLabel}
        </span>
      </div>
      <Button variant="brand" onClick={() => onSelect()} disabled={disabled}>
        {t('start', 'Inizia')}
      </Button>
    </li>
  );
}
